import React, { useState, useEffect } from "react";
import { useContext } from "react";
import { Formik, Form, ErrorMessage, useFormikContext } from "formik";
import Dashelement from "components/Dashboard/Dashelement";
import "./profileform.css";
import { BlankImg } from "assets/images";
import * as Yup from "yup";
import { useDropzone } from "react-dropzone";
import AuthContext from "context/AuthContext";
import RequiredLabel from "components/Forms/FormComponents/RequiredLabel";
import { EditOutlined } from "@ant-design/icons";

const ImagePreview = ({ setAvatar }) => {
  const { values } = useFormikContext();

  useEffect(() => {
    if (values.imageFile && values.imageFile.length > 0) {
      const file = values.imageFile[0];
      const reader = new FileReader();

      reader.onload = (e) => setAvatar(e.target.result);
      reader.readAsDataURL(file);
    }
  }, [values.imageFile, setAvatar]);

  return null;
};

const ProfileInfo = (props) => {
  const [avatar, setAvatar] = useState(BlankImg);
  const { user } = useContext(AuthContext);
  const isStoreAdmin = user?.role === 2;
  const readOnly = props.isCompleted && !props.isEditing;
  const data = props.data || {};

  React.useEffect(() => {
    if (data.profile_picture?.file) {
      setAvatar(data.profile_picture.file);
    } else {
      setAvatar(BlankImg);
    }
  }, [data.profile_picture?.file]);

  const FormValidate = isStoreAdmin
    ? Yup.object().shape({
        imageFile: Yup.array().notRequired(),
      })
    : Yup.object().shape({
        imageFile: Yup.array()
          .min(1, "Profile picture is required")
          .required("Profile picture is required"),
      });

  const handleSubmit = (values) => {
    props.next(values, false, true);
  };

  return (
    <Formik
      initialValues={props.data || {}}
      onSubmit={handleSubmit}
      validationSchema={FormValidate}
    >
      {({ values, setFieldValue }) => {
        const isImageUploaded =
          Array.isArray(values.imageFile) && values.imageFile.length > 0;

        const disableButton = !isStoreAdmin && !isImageUploaded;

        return (
          <Form>
            <ImagePreview setAvatar={setAvatar} />

            <Dashelement className="features profile-info-form-first">
              <div className="container">
                <div className="information-text wrapper">
                  <RequiredLabel
                    label={
                      <h3 className="mb-0">
                        Personal Information
                      </h3>
                    }
                    required={!isStoreAdmin}
                  />
                </div>

                <div className="row">
                  <div className="col-sm-12 col-md-6 mt-3 profile_picture_wrap">
                    <img src={avatar} className="img" alt="profile preview" />
                    <ErrorMessage
                      component="div"
                      name="imageFile"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="col-sm-12 col-md-6 mt-3 d-flex profile_picture_upload_wrap justify-content-center">
                    <UploadImage
                      setFieldValue={setFieldValue}
                      value="imageFile"
                      className="uploader-parts"
                      title="Upload Your Photo"
                      maxSize={10000000}
                      disabled={readOnly}
                      body={
                        <div className="mb-3">
                          Requirements:
                          <ul>
                            <li>.jpg, .png, or .gif file type</li>
                            <li>less than 10MB</li>
                            <li>file name is less than 100 characters</li>
                          </ul>
                        </div>
                      }
                      footer="Click here or drag image here."
                      accept={{ "image/*": [] }}
                    />
                  </div>
                </div>
              </div>
            </Dashelement>

            <div className="button-place text-end d-flex gap-2 justify-content-end">
              {readOnly ? (
                <>
                  {props.onStartEdit && <button type="button" className="btn btn-outline-primary" onClick={props.onStartEdit}><EditOutlined />Edit</button>}
                  {props.onNextStep && <button type="button" className="btn btn-primary" onClick={props.onNextStep}>Next</button>}
                </>
              ) : (
                <>
                  {props.isEditing && props.onCancelEdit && (
                    <button type="button" className="btn btn-outline-secondary" onClick={props.onCancelEdit}>Cancel</button>
                  )}
                  <button
                    className={`btn ${
                      disableButton ? "btn-secondary" : "btn-primary"
                    }`}
                    type="submit"
                    disabled={disableButton}
                  >
                    {props.isEditing ? "Save" : "Next"}
                  </button>
                </>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export const UploadImage = (props) => {
  const { setFieldValue } = props;
  const [errors, setErrors] = useState("");

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: props.accept,
    maxFiles: 1,
    multiple: false,
    maxSize: props.maxSize,
    disabled: props.disabled,
    onDrop: (acceptedFiles, fileRejections) => {
      if (acceptedFiles.length > 0) {
        setErrors("");
        setFieldValue(props.value, acceptedFiles);
      }

      if (fileRejections.length > 0) {
        fileRejections.forEach(({ errors }) => {
          errors.forEach((err) => {
            if (err.code === "file-too-large") {
              setErrors("Error: File is larger than 10MB");
            }
            if (err.code === "file-invalid-type") {
              setErrors(`Error: ${err.message}`);
            }
          });
        });
      }
    },
  });

  return (
    <div className="upload-field">
      <div {...getRootProps({ className: "dropzone", style: props.disabled ? { pointerEvents: "none", opacity: 0.9 } : {} })}>
        <input {...getInputProps()} />

        <div className={`${props.className} text-center`}>
          <div className="upload-text justify-content-center">
            <h5 className="mb-3">
              {isDragActive ? "Drop the files here ..." : props.title}
            </h5>

            {props.body}
            {errors && <p style={{ color: "red" }}>{errors}</p>}
            <p className="mb-3" style={{ color: "#FF3030" }}>
              {props.footer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileInfo;
